import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";

import { curveImportFieldHelp } from "../../app/guidance/fieldHelpRegistry";
import { guided } from "../../app/guidance/fieldHelpRegistryCore";
import { readableCurveError } from "./errors";

import type { CurveImportApi } from "./api";
import type {
  ChartRegion,
  CurveDraftDocument,
  CurveImportRead,
  PixelPoint,
} from "./types";

interface GraphRegionSelectorProps {
  api: CurveImportApi;
  disabled?: boolean;
  document: CurveDraftDocument;
  importId: string;
  onBusyChange?: ((busy: boolean) => void) | undefined;
  onDirtyChange?: ((dirty: boolean) => void) | undefined;
  onError: (message: string) => void;
  onImported: (imported: CurveImportRead) => void;
  onNotice: (message: string) => void;
  pageCount: number | null;
}

interface ImageSize {
  height: number;
  width: number;
}

interface SavedSelection {
  page: number;
  region: ChartRegion | null;
}

const MIN_BOX_SIZE_PX = 64;

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

function sameRegion(
  left: ChartRegion | null,
  right: ChartRegion | null,
): boolean {
  if (left === null || right === null) return left === right;
  return (
    left.page_number === right.page_number &&
    left.left_px === right.left_px &&
    left.top_px === right.top_px &&
    left.right_px === right.right_px &&
    left.bottom_px === right.bottom_px
  );
}

function validRegion(
  region: ChartRegion | null,
  size: ImageSize | null,
): boolean {
  if (region === null || size === null) return false;
  return (
    region.left_px >= 0 &&
    region.top_px >= 0 &&
    region.right_px <= size.width &&
    region.bottom_px <= size.height &&
    region.right_px - region.left_px >= MIN_BOX_SIZE_PX &&
    region.bottom_px - region.top_px >= MIN_BOX_SIZE_PX
  );
}

export function GraphRegionSelector({
  api,
  disabled = false,
  document,
  importId,
  onBusyChange,
  onDirtyChange,
  onError,
  onImported,
  onNotice,
  pageCount,
}: GraphRegionSelectorProps) {
  const initialPage =
    document.selected_chart?.page_number ?? document.selected_page ?? 1;
  const [page, setPage] = useState(initialPage);
  const [imageSize, setImageSize] = useState<ImageSize | null>(null);
  const [region, setRegion] = useState<ChartRegion | null>(
    document.selected_chart?.page_number === initialPage
      ? document.selected_chart
      : null,
  );
  const [saving, setSaving] = useState(false);
  const dragStart = useRef<PixelPoint | null>(null);
  const activePointerId = useRef<number | null>(null);
  const regionBeforeDrag = useRef<ChartRegion | null>(null);

  const previousSavedSelection = useRef<SavedSelection>({
    page: initialPage,
    region:
      document.selected_chart?.page_number === initialPage
        ? document.selected_chart
        : null,
  });
  const savedRegion =
    document.selected_chart?.page_number === page
      ? document.selected_chart
      : null;
  const entirePageRegion: ChartRegion | null =
    imageSize === null
      ? null
      : {
          bottom_px: imageSize.height,
          label: "Primary chart",
          left_px: 0,
          page_number: page,
          right_px: imageSize.width,
          top_px: 0,
        };

  const pointFromEvent = (
    event: ReactPointerEvent<SVGSVGElement>,
  ): PixelPoint | null => {
    if (
      imageSize === null ||
      !Number.isFinite(event.clientX) ||
      !Number.isFinite(event.clientY)
    ) {
      return null;
    }
    const bounds = event.currentTarget.getBoundingClientRect();
    return {
      x: clamp(
        ((event.clientX - bounds.left) / Math.max(bounds.width, 1)) *
          imageSize.width,
        0,
        imageSize.width,
      ),
      y: clamp(
        ((event.clientY - bounds.top) / Math.max(bounds.height, 1)) *
          imageSize.height,
        0,
        imageSize.height,
      ),
    };
  };

  const regionFromCorners = (
    start: PixelPoint,
    end: PixelPoint,
  ): ChartRegion => ({
    bottom_px: Math.max(start.y, end.y),
    label: "Primary chart",
    left_px: Math.min(start.x, end.x),
    page_number: page,
    right_px: Math.max(start.x, end.x),
    top_px: Math.min(start.y, end.y),
  });

  const startDrag = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (
      disabled ||
      saving ||
      event.isPrimary === false ||
      activePointerId.current !== null ||
      (event.pointerType === "mouse" && event.button !== 0)
    ) {
      return;
    }
    const point = pointFromEvent(event);
    if (point === null) return;
    regionBeforeDrag.current = region;
    dragStart.current = point;
    activePointerId.current = event.pointerId;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setRegion(regionFromCorners(point, point));
  };

  const continueDrag = (event: ReactPointerEvent<SVGSVGElement>) => {
    const start = dragStart.current;
    if (start === null || event.pointerId !== activePointerId.current) return;
    const point = pointFromEvent(event);
    if (point === null) return;
    event.preventDefault();
    setRegion(regionFromCorners(start, point));
  };

  const finishDrag = (event: ReactPointerEvent<SVGSVGElement>) => {
    const start = dragStart.current;
    if (start === null || event.pointerId !== activePointerId.current) return;
    const point = pointFromEvent(event);
    const candidate = point === null ? null : regionFromCorners(start, point);
    setRegion(
      validRegion(candidate, imageSize) ? candidate : regionBeforeDrag.current,
    );
    dragStart.current = null;
    activePointerId.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const cancelDrag = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (
      dragStart.current === null ||
      event.pointerId !== activePointerId.current
    ) {
      return;
    }
    dragStart.current = null;
    activePointerId.current = null;
    setRegion(regionBeforeDrag.current);
  };

  const startKeyboardBox = () => {
    if (imageSize === null) return;
    setRegion({
      bottom_px: imageSize.height * 0.9,
      label: "Primary chart",
      left_px: imageSize.width * 0.1,
      page_number: page,
      right_px: imageSize.width * 0.9,
      top_px: imageSize.height * 0.1,
    });
  };

  const updateBoundary = (
    field: "bottom_px" | "left_px" | "right_px" | "top_px",
    value: number,
  ) => {
    if (region === null || imageSize === null || !Number.isFinite(value))
      return;
    const next = { ...region };
    if (field === "left_px") {
      next.left_px = clamp(value, 0, next.right_px - 1);
    } else if (field === "right_px") {
      next.right_px = clamp(value, next.left_px + 1, imageSize.width);
    } else if (field === "top_px") {
      next.top_px = clamp(value, 0, next.bottom_px - 1);
    } else {
      next.bottom_px = clamp(value, next.top_px + 1, imageSize.height);
    }
    setRegion(next);
  };

  const saveRegion = async (
    selectedRegion: ChartRegion | null,
    notice: string,
  ) => {
    if (
      !validRegion(selectedRegion, imageSize) ||
      selectedRegion === null ||
      saving ||
      disabled
    ) {
      return;
    }
    setSaving(true);
    onBusyChange?.(true);
    try {
      const imported = await api.updateDraft(importId, {
        ...document,
        selected_chart: selectedRegion,
        selected_page: selectedRegion.page_number,
      });
      onImported(imported);
      onNotice(notice);
    } catch (error: unknown) {
      onError(readableCurveError(error, "Graph area could not be saved."));
    } finally {
      setSaving(false);
      onBusyChange?.(false);
    }
  };

  const save = () => saveRegion(region, "Graph area saved.");

  const selectEntirePdf = async () => {
    if (entirePageRegion === null) return;
    setRegion(entirePageRegion);
    await saveRegion(entirePageRegion, "Full page saved.");
  };

  const hasValidRegion = validRegion(region, imageSize);
  const entirePageActionLabel =
    pageCount !== null && pageCount > 1
      ? "Use entire page"
      : "Use entire PDF";

  useEffect(() => {
    const nextPage =
      document.selected_chart?.page_number ?? document.selected_page ?? 1;
    const nextRegion =
      document.selected_chart?.page_number === nextPage
        ? document.selected_chart
        : null;
    const previous = previousSavedSelection.current;
    if (previous.page === nextPage && sameRegion(previous.region, nextRegion)) {
      return;
    }
    previousSavedSelection.current = { page: nextPage, region: nextRegion };

    const previousRegionOnPage =
      previous.region?.page_number === page ? previous.region : null;
    const localWasDirty = !sameRegion(region, previousRegionOnPage);
    const localMatchesNext =
      page === nextPage && sameRegion(region, nextRegion);
    if (localWasDirty && !localMatchesNext) return;

    if (page !== nextPage) setImageSize(null);
    setPage(nextPage);
    setRegion(nextRegion);
  }, [document.selected_chart, document.selected_page, page, region]);

  useEffect(() => {
    onDirtyChange?.(!sameRegion(region, savedRegion));
  }, [onDirtyChange, region, savedRegion]);

  useEffect(
    () => () => {
      onDirtyChange?.(false);
    },
    [onDirtyChange],
  );

  const clear = async () => {
    if (disabled || saving) return;
    if (savedRegion === null) {
      setRegion(null);
      return;
    }
    setSaving(true);
    onBusyChange?.(true);
    try {
      const imported = await api.updateDraft(importId, {
        ...document,
        selected_chart: null,
        selected_page: page,
      });
      setRegion(null);
      onImported(imported);
      onNotice("Graph cleared.");
    } catch (error: unknown) {
      onError(readableCurveError(error, "Graph could not be cleared."));
    } finally {
      setSaving(false);
      onBusyChange?.(false);
    }
  };

  return (
    <div className="curve-graph-selector">
      <p className="curve-graph-selector__instruction">
        Drag a box around the inside graph grid.
      </p>
      <div className="curve-button-row curve-graph-selector__page-actions">
        {pageCount !== null && pageCount > 1 ? (
          <label className="curve-graph-selector__page">
            Page
            <select
              {...guided(curveImportFieldHelp.chartPage, "graph-region")}
              aria-label="Graph page"
              disabled={disabled || saving}
              value={page}
              onChange={(event) => {
                const nextPage = Number(event.target.value);
                setPage(nextPage);
                setImageSize(null);
                setRegion(
                  document.selected_chart?.page_number === nextPage
                    ? document.selected_chart
                    : null,
                );
              }}
            >
              {Array.from({ length: pageCount }, (_, index) => index + 1).map(
                (pageNumber) => (
                  <option key={pageNumber} value={pageNumber}>
                    {pageNumber}
                  </option>
                ),
              )}
            </select>
          </label>
        ) : null}
        <button
          disabled={
            disabled ||
            saving ||
            entirePageRegion === null ||
            sameRegion(entirePageRegion, savedRegion)
          }
          type="button"
          onClick={() => void selectEntirePdf()}
        >
          {saving ? "Saving…" : entirePageActionLabel}
        </button>
      </div>
      <div className="curve-graph-selector__page-image">
        <img
          alt={"Vendor sheet page " + page}
          draggable="false"
          src={api.pageUrl(importId, page)}
          onError={() => onError("The vendor sheet page could not be shown.")}
          onLoad={(event) => {
            const image = event.currentTarget;
            if (image.naturalWidth > 0 && image.naturalHeight > 0) {
              setImageSize({
                height: image.naturalHeight,
                width: image.naturalWidth,
              });
            }
          }}
        />
        {imageSize === null ? null : (
          <svg
            aria-label="Graph area selector"
            className="curve-graph-selector__overlay"
            preserveAspectRatio="none"
            role="img"
            viewBox={"0 0 " + imageSize.width + " " + imageSize.height}
            onPointerCancel={cancelDrag}
            onPointerDown={startDrag}
            onPointerMove={continueDrag}
            onPointerUp={finishDrag}
          >
            <title>Drag to select the graph area</title>
            {region === null ? null : (
              <rect
                height={Math.max(0, region.bottom_px - region.top_px)}
                pointerEvents="none"
                width={Math.max(0, region.right_px - region.left_px)}
                x={region.left_px}
                y={region.top_px}
              />
            )}
          </svg>
        )}
      </div>
      <p aria-live="polite" className="curve-graph-selector__status">
        {hasValidRegion ? "Graph selected." : "Select the graph."}
      </p>
      <details className="curve-graph-selector__fine-tune">
        <summary>Fine tune box</summary>
        {region === null ? (
          <button
            disabled={disabled || imageSize === null || saving}
            type="button"
            onClick={startKeyboardBox}
          >
            Start box
          </button>
        ) : (
          <div className="curve-graph-selector__fields">
            {(
              [
                ["left_px", "Left"],
                ["top_px", "Top"],
                ["right_px", "Right"],
                ["bottom_px", "Bottom"],
              ] as const
            ).map(([field, label]) => (
              <label key={field}>
                {label}
                <input
                  {...guided(curveImportFieldHelp.chartBoundary, field)}
                  aria-label={"Graph " + label.toLowerCase()}
                  disabled={disabled || saving}
                  min="0"
                  step="1"
                  type="number"
                  value={Math.round(region[field])}
                  onChange={(event) =>
                    updateBoundary(field, Number(event.target.value))
                  }
                />
              </label>
            ))}
          </div>
        )}
      </details>
      <div className="curve-button-row curve-graph-selector__actions">
        <button
          className="curve-primary-action"
          disabled={
            disabled ||
            saving ||
            !hasValidRegion ||
            sameRegion(region, savedRegion)
          }
          type="button"
          onClick={() => void save()}
        >
          {saving ? "Saving…" : "Use this graph"}
        </button>
        <button
          disabled={
            disabled || saving || (region === null && savedRegion === null)
          }
          type="button"
          onClick={() => void clear()}
        >
          Clear box
        </button>
      </div>
    </div>
  );
}
